<template>
	<span v-if="false"></span>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, useSlots, watch } from 'vue';
import type { TooltipAction, TooltipOptions } from './Tooltip.model';
import { tooltipService } from './Tooltip.service';

defineOptions({
	name: 'ArTooltip',
});

const props = withDefaults(
	defineProps<
		TooltipOptions & {
			open?: boolean;
			showOnParentHover?: boolean;
			anchorEl?: HTMLElement | null;
		}
	>(),
	{
		text: '',
		html: '',
		color: 'default',
		placement: 'top',
		interactive: false,
		multiline: false,
		maxWidth: 320,
		showArrow: true,
		actions: () => [],
		disabled: false,
		open: true,
		showOnParentHover: false,
		anchorEl: null,
	}
);

const emit = defineEmits<{
	(e: 'action-click', action: TooltipAction): void;
	(e: 'hover-change', hovered: boolean): void;
}>();

const slots = useSlots();
const tooltipId = `tooltip-${Math.random().toString(36).slice(2, 11)}`;
const hoverOpen = ref(false);

const hasActions = computed(
	() => Array.isArray(props.actions) && props.actions.length > 0
);
const isMultiline = computed(
	() => props.multiline || (props.text || '').includes('\n')
);
const hasContent = computed(() =>
	Boolean(props.text || props.html || hasActions.value || slots.content)
);
const shouldBeOpen = computed(
	() => props.open || (props.showOnParentHover && hoverOpen.value)
);

const onActionClick = (action: TooltipAction) => {
	emit('action-click', action);
};

const onHoverChange = (hovered: boolean) => {
	emit('hover-change', hovered);
};

const onAnchorEnter = () => {
	hoverOpen.value = true;
};

const onAnchorLeave = () => {
	hoverOpen.value = false;
};

const attachAnchorListeners = (anchor: HTMLElement | null) => {
	if (!anchor || !props.showOnParentHover) {
		return;
	}

	anchor.addEventListener('mouseenter', onAnchorEnter);
	anchor.addEventListener('mouseleave', onAnchorLeave);
	anchor.addEventListener('focusin', onAnchorEnter);
	anchor.addEventListener('focusout', onAnchorLeave);
};

const detachAnchorListeners = (anchor: HTMLElement | null) => {
	if (!anchor) {
		return;
	}

	anchor.removeEventListener('mouseenter', onAnchorEnter);
	anchor.removeEventListener('mouseleave', onAnchorLeave);
	anchor.removeEventListener('focusin', onAnchorEnter);
	anchor.removeEventListener('focusout', onAnchorLeave);
};

const syncTooltip = () => {
	if (
		props.disabled ||
		!shouldBeOpen.value ||
		!hasContent.value ||
		!props.anchorEl ||
		!document.body.contains(props.anchorEl)
	) {
		tooltipService.close(tooltipId);
		return;
	}

	tooltipService.open({
		id: tooltipId,
		anchor: props.anchorEl,
		text: props.text,
		html: props.html,
		color: props.color,
		placement: props.placement,
		multiline: isMultiline.value,
		maxWidth: props.maxWidth,
		showArrow: props.showArrow,
		actions: props.actions,
		disabled: props.disabled,
		renderContent: slots.content || null,
		onActionClick,
		onHoverChange,
	});
};

watch(
	() => [
		props.anchorEl,
		props.open,
		props.showOnParentHover,
		hoverOpen.value,
		props.text,
		props.html,
		props.color,
		props.placement,
		props.multiline,
		props.maxWidth,
		props.showArrow,
		props.disabled,
		props.actions,
	],
	syncTooltip,
	{ immediate: true, deep: true }
);

watch(
	() => props.anchorEl,
	(nextAnchor, previousAnchor) => {
		detachAnchorListeners(previousAnchor || null);
		attachAnchorListeners(nextAnchor || null);
	},
	{ immediate: true }
);

watch(
	() => props.showOnParentHover,
	(enabled) => {
		if (!enabled) {
			hoverOpen.value = false;
			detachAnchorListeners(props.anchorEl || null);
			return;
		}

		attachAnchorListeners(props.anchorEl || null);
	},
	{ immediate: true }
);

onBeforeUnmount(() => {
	detachAnchorListeners(props.anchorEl || null);
	tooltipService.close(tooltipId);
});
</script>
